'use client';

import { useState } from 'react';
import { getCoverImageTheme } from 'app/blog/[domain]/_components/cover-image-theme';
import { defaultSortByAtom } from 'app/blog/[domain]/_components/design-state';
import { IconArrowUpRight } from 'icon/Arrows/arrow-up-right';
import { useAtomValue } from 'jotai';
import { useFormatter, useTranslations } from 'next-intl';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'ui';
import type { NormalizedSortBy } from '../_components/types';
import { MAX_POST_PER_PAGE } from '../_server/constants';
import type { PostCardFields } from '../_server/types';
import { BlogHero } from './blog-hero';
import { BlogPagination } from './blog-pagination';
import { BlogTabList, BlogTabs } from './blog-tabs';
import { PostLink } from './post-link';
import { SortByMenu } from './sort-post-menu';

type FieldnotesNotebookProps = {
  posts: PostCardFields[];
  designPosts?: PostCardFields[];
  featuredPost: PostCardFields;
  tags: string[];
  tag: string;
  index: number;
  sortBy: NormalizedSortBy;
  totalPostCount: number;
  design?: boolean;
};

export function FieldnotesNotebook({
  posts,
  designPosts,
  featuredPost,
  tags,
  tag,
  index,
  sortBy,
  totalPostCount,
  design,
}: FieldnotesNotebookProps) {
  const t = useTranslations('Blog.Fieldnotes');
  const defaultSort = useAtomValue(defaultSortByAtom);
  const entries =
    design && designPosts
      ? designPosts
          .toSorted((a, b) => {
            if (defaultSort === 'titleAsc')
              return a.title.localeCompare(b.title);
            if (defaultSort === 'titleDesc')
              return b.title.localeCompare(a.title);
            const difference =
              new Date(b.publishedAt).getTime() -
              new Date(a.publishedAt).getTime();
            return defaultSort === 'leastRecent' ? -difference : difference;
          })
          .slice(0, MAX_POST_PER_PAGE)
      : posts;
  const initialEntry =
    entries.find((post) => post.id === featuredPost.id) ||
    entries[0] ||
    featuredPost;
  const [selectedId, setSelectedId] = useState(initialEntry.id);
  const selectedEntry =
    entries.find((post) => post.id === selectedId) || initialEntry;

  return (
    <div className="fieldnotes-notebook">
      <BlogTabs tag={tag} sortBy={sortBy}>
        <div className="fieldnotes-book">
          <section className="fieldnotes-contents" aria-label={t('Contents')}>
            <div className="fieldnotes-introduction">
              <BlogHero />
            </div>
            <div className="fieldnotes-contents-heading">
              <h2>{t('Contents')}</h2>
              <span>{t('EntryCount', { count: totalPostCount })}</span>
            </div>
            <div className="fieldnotes-filters">
              <BlogTabList tags={tags} />
            </div>
            <div className="fieldnotes-sort">
              <SortByMenu
                index={index}
                tag={tag}
                value={sortBy}
                design={design}
              />
            </div>
            <ol className="fieldnotes-index">
              {entries.map((post) => (
                <li
                  key={post.id}
                  data-selected={post.id === selectedEntry.id}
                  onMouseEnter={() => setSelectedId(post.id)}
                  onFocus={() => setSelectedId(post.id)}
                >
                  <PostLink
                    href={`/post/${post.slug}_${post.id}`}
                    className="fieldnotes-index-link"
                  >
                    <EntryDate date={post.publishedAt} />
                    <span className="fieldnotes-index-title">{post.title}</span>
                    <IconArrowUpRight aria-hidden="true" />
                  </PostLink>
                </li>
              ))}
            </ol>
            <Accordion
              type="single"
              collapsible
              defaultValue={initialEntry.id}
              className="fieldnotes-mobile-entries"
            >
              {entries.map((post) => (
                <AccordionItem
                  key={post.id}
                  value={post.id}
                  className="fieldnotes-mobile-entry"
                >
                  <AccordionTrigger className="fieldnotes-entry-trigger">
                    <span>
                      <EntryDate date={post.publishedAt} />
                      <span className="fieldnotes-index-title">
                        {post.title}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <EntryPreview post={post} mobile />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            {totalPostCount > MAX_POST_PER_PAGE && (
              <BlogPagination
                current={index}
                tag={tag}
                sortBy={sortBy}
                total={Math.ceil(totalPostCount / MAX_POST_PER_PAGE)}
              />
            )}
          </section>
          <aside className="fieldnotes-preview" aria-label={t('Preview')}>
            <p className="sr-only" aria-live="polite">
              {selectedEntry.title}
            </p>
            <EntryPreview post={selectedEntry} />
          </aside>
        </div>
      </BlogTabs>
    </div>
  );
}

function EntryPreview({
  post,
  mobile,
}: {
  post: PostCardFields;
  mobile?: boolean;
}) {
  const t = useTranslations('Blog.PostCard');
  return (
    <div className="fieldnotes-entry-preview">
      <PostLink
        href={`/post/${post.slug}_${post.id}`}
        className="fieldnotes-photo-link"
      >
        <img
          key={post.id}
          alt={post.title}
          src={post.coverImage}
          data-theme-cover={getCoverImageTheme(post.coverImage)}
          className="fieldnotes-entry-photo"
          loading={mobile ? 'lazy' : 'eager'}
          fetchPriority={mobile ? 'auto' : 'high'}
        />
      </PostLink>
      <div className="fieldnotes-caption">
        <div className="fieldnotes-entry-meta">
          <EntryDate date={post.publishedAt} />
          {post.tags[0] && <span>{post.tags[0]}</span>}
        </div>
        {!mobile && (
          <h2>
            <PostLink href={`/post/${post.slug}_${post.id}`}>
              {post.title}
            </PostLink>
          </h2>
        )}
        {post.excerpt && (
          <p className="fieldnotes-entry-excerpt">{post.excerpt}</p>
        )}
        <PostLink
          href={`/post/${post.slug}_${post.id}`}
          className="fieldnotes-read-link"
        >
          {t('ReadPost')} <IconArrowUpRight aria-hidden="true" />
        </PostLink>
      </div>
    </div>
  );
}

function EntryDate({ date }: { date: Date | string }) {
  const format = useFormatter();
  return (
    <time dateTime={new Date(date).toISOString()}>
      {format.dateTime(new Date(date), {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })}
    </time>
  );
}
